import React, { useEffect, useState } from 'react';
import { Box, TextInput, Group, ActionIcon, Divider, MultiSelect, Text } from '@mantine/core';
import { X, Plus } from 'tabler-icons-react';

function MissingNumbersEpreuve({ form, index, onRemove }) {
    const isEditing = Boolean(form.values.epreuves[index].id);
    const [errors, setErrors] = useState({});

    // Initialize consigne if it doesn't exist or handle editing
    useEffect(() => {
        if (isEditing) {
            // If editing, parse the consigne items and ensure they have the correct structure
            const existingConsigne = form.values.epreuves[index].consigne.map(item => {
                if (typeof item === 'string') {
                    const parsed = JSON.parse(item);
                    return {
                        value: parsed.value,
                        type: parsed.type || 'number'
                    };
                }
                return item;
            });
            form.setFieldValue(`epreuves.${index}.consigne`, existingConsigne);

            // Initialize correction_dp with all numbers from consigne
            const allNumbers = existingConsigne
                .filter(item => item.value !== "")
                .map(item => item.value);
            form.setFieldValue(`epreuves.${index}.correction_dp`, allNumbers);
        } else if (!form.values.epreuves[index].consigne) {
            // If new epreuve, initialize with empty values
            form.setFieldValue(`epreuves.${index}.consigne`, [
                { value: "", type: "number" },
                { value: "", type: "number" }
            ]);
        }
    }, [isEditing]);

    // Validate if a number is a multiple of 5
    const isMultipleOf5 = (number) => {
        return number % 5 === 0;
    };

    // Check for duplicate numbers
    const hasDuplicateNumbers = (numbers) => {
        const uniqueNumbers = new Set(numbers);
        return uniqueNumbers.size !== numbers.length;
    };

    // Handle hiding numbers and update correction_dp
    const handleHideNumbers = (numbersToHide) => {
        // Update the type of numbers in consigne to 'hidden'
        const updatedConsigne = form.values.epreuves[index].consigne.map(item => ({
            ...item,
            type: numbersToHide.includes(item.value) ? 'hidden' : 'number'
        }));

        form.setFieldValue(`epreuves.${index}.consigne`, updatedConsigne);

        // Update correction_dp with all numbers from consigne
        const allNumbers = updatedConsigne
            .filter(item => item.value !== "")
            .map(item => item.value);
        form.setFieldValue(`epreuves.${index}.correction_dp`, allNumbers);
    };

    // Handle input change without validation
    const handleInputChange = (idx, value) => {
        form.setFieldValue(`epreuves.${index}.consigne.${idx}.value`, value);
        // Clear error for this input when user starts typing
        setErrors(prev => ({ ...prev, [idx]: '' }));
    };

    // Validate and update number on blur
    const handleInputBlur = (idx, value) => {
        if (value === "") {
            return;
        }

        const numValue = Number(value);

        // Validate if the number is a multiple of 5
        if (!isMultipleOf5(numValue)) {
            setErrors(prev => ({ ...prev, [idx]: 'Le nombre doit être un multiple de 5' }));
            form.setFieldValue(`epreuves.${index}.consigne.${idx}.value`, "");
            return;
        }

        // Get all current numbers for duplicate check
        const currentNumbers = form.values.epreuves[index].consigne
            .map((item, i) => i !== idx ? Number(item.value) : null)
            .filter(num => !isNaN(num) && num !== 0 && num !== null);

        // Check for duplicates
        if (currentNumbers.includes(numValue)) {
            setErrors(prev => ({ ...prev, [idx]: 'Ce nombre existe déjà dans la liste' }));
            form.setFieldValue(`epreuves.${index}.consigne.${idx}.value`, "");
            return;
        }

        // Clear error for this input if validation passes
        setErrors(prev => ({ ...prev, [idx]: '' }));

        // Update correction_dp with all numbers from consigne
        const numbers = form.values.epreuves[index].consigne
            .filter(item => item.value !== "")
            .map(item => item.value);

        if (numbers.length >= 2) {
            form.setFieldValue(`epreuves.${index}.correction_dp`, numbers);
        }
    };

    // Generate options for selection based on consigne values
    const selectOptions = form.values.epreuves[index].consigne
        .filter(item => item.value !== "")
        .map(item => ({
            value: item.value,
            label: item.value
        }));

    // Get currently hidden numbers
    const getHiddenNumbers = () => {
        return form.values.epreuves[index].consigne
            .filter(item => item.type === 'hidden')
            .map(item => item.value);
    };

    return (
        <Box key={index}>
            <TextInput
                placeholder="Complétez la liste en comptant par 5"
                mb={10}
                radius={10}
                {...form.getInputProps(`epreuves.${index}.titre`)}
            />
            <Group position="apart" align="center" noWrap>
                <Group spacing="xs" align="top">
                    {form.values.epreuves[index].consigne?.map((item, idx) => (
                        <Box key={idx}>
                            <TextInput
                                type="number"
                                placeholder="Nombre"
                                radius={10}
                                w={80}
                                value={item.value}
                                onChange={(e) => handleInputChange(idx, e.target.value)}
                                onBlur={(e) => handleInputBlur(idx, e.target.value)}
                            />
                            {errors[idx] && (
                                <Text color="red" size="xs" mt={4}>
                                    {errors[idx]}
                                </Text>
                            )}
                        </Box>
                    ))}
                    <ActionIcon
                        color="green"
                        onClick={() =>
                            form.insertListItem(`epreuves.${index}.consigne`, {
                                value: "",
                                type: "number",
                            })
                        }
                    >
                        <Plus size="1rem" />
                    </ActionIcon>
                </Group>
                {selectOptions.length > 0 && (
                    <Group spacing="sm" noWrap>
                        <MultiSelect
                            data={selectOptions}
                            placeholder="Sélectionnez les nombres à cacher"
                            radius={10}
                            w={250}
                            value={getHiddenNumbers()}
                            onChange={handleHideNumbers}
                        />
                        <ActionIcon color="red" onClick={() => onRemove(index)}>
                            <X size="1rem" />
                        </ActionIcon>
                    </Group>
                )}
            </Group>
            <Divider my={20} />
        </Box>
    );
}

export default MissingNumbersEpreuve; 